import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useDeployMetadata, useDeploymentProgress } from "@/hooks/deployment";
import { useMetadataEditorStore, type EditorTab } from "@/stores/metadataEditorStore";

interface UseEditorDeploymentProps {
  orgId: string;
  tabs: EditorTab[];
}

interface UseEditorDeploymentReturn {
  isDeploying: boolean;
  deploymentMessage: string | null;
  editedTabs: EditorTab[];
  editedTabsCount: number;
  handleDeploy: () => Promise<void>;
}

export function useEditorDeployment({
  orgId,
  tabs,
}: UseEditorDeploymentProps): UseEditorDeploymentReturn {
  const markSaved = useMetadataEditorStore((state) => state.markSaved);

  // Deploy mutation and progress
  const deployMutation = useDeployMetadata();
  const {
    status: deploymentStatus,
    message: deploymentMessage,
    errorMessage: deploymentErrorMessage,
    isComplete: isDeploymentComplete,
    connect: connectToProgress,
    disconnect: disconnectFromProgress,
  } = useDeploymentProgress();

  // Deploy state
  const [isDeploying, setIsDeploying] = useState(false);
  // Use ref for file count to avoid stale closure issues
  const deployingFilesCountRef = useRef<number>(0);

  // Get edited tabs (files ready to deploy)
  const editedTabs = tabs.filter((t) => t.isEdited);
  const editedTabsCount = editedTabs.length;

  // Handle deploy click
  const handleDeploy = useCallback(async () => {
    if (editedTabs.length === 0 || !orgId || isDeploying) return;

    const metadataItemIds = editedTabs.map((tab) => tab.file.id);

    // Store the count in ref before deployment starts
    deployingFilesCountRef.current = metadataItemIds.length;
    setIsDeploying(true);

    try {
      const deploymentResult = await deployMutation.mutateAsync({
        organizationId: orgId,
        metadataItemIds,
        deployOptions: {
          rollbackOnError: true,
        },
      });

      console.log("🚀 Deployment started:", deploymentResult?.id);

      // Connect to SSE for real-time progress
      if (deploymentResult?.id) {
        connectToProgress(deploymentResult.id);
      }
    } catch (error: any) {
      setIsDeploying(false);
      deployingFilesCountRef.current = 0;
      toast.error(`Deployment failed: ${error.message}`, {
        duration: 5000,
        position: "top-right",
      });
    }
  }, [editedTabs, orgId, isDeploying, deployMutation, connectToProgress]);

  // Handle deployment completion from SSE
  useEffect(() => {
    if (isDeploymentComplete) {
      if (deploymentStatus === "succeeded") {
        toast.success(
          `Successfully deployed ${deployingFilesCountRef.current} file(s)!`,
          {
            duration: 4000,
            position: "top-right",
          },
        );

        // Mark deployed tabs as no longer edited
        tabs
          .filter((t) => t.isEdited)
          .forEach((tab) => {
            markSaved(tab.id, tab.content, false);
          });
      } else if (deploymentStatus === "failed") {
        toast.error(
          `Deployment failed: ${deploymentErrorMessage || "Unknown error"}`,
          {
            duration: 5000,
            position: "top-right",
          },
        );
      } else if (deploymentStatus === "cancelled") {
        toast.error("Deployment was cancelled", {
          duration: 4000,
          position: "top-right",
        });
      }

      // Reset state
      setIsDeploying(false);
      deployingFilesCountRef.current = 0;
      disconnectFromProgress();
    }
  }, [
    isDeploymentComplete,
    deploymentStatus,
    deploymentErrorMessage,
    tabs,
    markSaved,
    disconnectFromProgress,
  ]);

  return {
    isDeploying,
    deploymentMessage,
    editedTabs,
    editedTabsCount,
    handleDeploy,
  };
}
